import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import PropTypes, { object } from 'prop-types'
import { Redirect } from 'react-router-dom'
import { useSearchParam } from 'hooks/useSearchParams'
import { useErrorHandler } from 'react-error-boundary'
import moment from 'moment'

import StepCompletionBar from 'components/Recommendations/Campaign/StepCompletionBar'
import { Goals } from './Steps'
import { DEFAULT_CAMPAIGN_BUDGET, DEFAULT_CAMPAIGN_GOAL, isCampaignRunning } from 'api/models'
import { fetchEvent } from 'api/events'
import publishedCampaignSetupsApi from 'domain/publishedCampaignSetups/api'
import SetupWrapper from './Steps/Setup/SetupWrapper'
import { useToast } from 'components/Shared/AppToast/ToastProvider'
import {
  useCampaignSetup,
  useCurrentStepDispatch,
  useCurrentStep,
} from '../Campaign/Context/CampaignSetupContext'
import { useCampaignSetupDispatch } from '../Campaign/Context/CampaignSetupContext'
import { useCampaignConfigDispatch } from '../Campaign/RunningCampaign/CampaignConfig/CampaignConfigProvider'
import { FdPublishedIcon } from 'images'
import {
  SETTINGS_PLATFORMS_ANCHOR,
  providerNamesForPlatforms,
} from 'domain/platform/permissionErrorTarget'
import { derivePlatformSelectionFromSetup, toApiPlatformField } from '../Campaign/platform'

const Step = (props) => {
  const { currentStep } = useCurrentStep()

  switch (currentStep) {
    case 1:
      return <Goals {...props} />
    case 2:
      return <SetupWrapper {...props} child={'Accounts'} />
    case 3:
      return <SetupWrapper {...props} child={'Ads'} />
    case 4:
      return <SetupWrapper {...props} child={'Summary'} />
    default:
      return null
  }
}

Step.propTypes = {
  event: PropTypes.object,
  initialGoal: PropTypes.string,
  initialBudget: PropTypes.number,
  onNext: PropTypes.func,
  onBack: PropTypes.func,
  boost: PropTypes.func,
  isFutureEvent: PropTypes.bool,
}

const CampaignStepWrapper = ({ eventId, eventState, fetchedCampaigns, resetKey }) => {
  const { t } = useTranslation()
  const [eventGoal, setEventGoal] = useState(DEFAULT_CAMPAIGN_GOAL)
  const [eventBudget, setEventBudget] = useState(DEFAULT_CAMPAIGN_BUDGET)
  const [campaigns, setCampaigns] = useState([])
  // listens for breakpoints desktop => mobile && vice versa
  const [viewWidth, setViewWidth] = useState(window.innerWidth)
  const { addNotification } = useToast()
  const searchParam = useSearchParam()

  const allCampaignsActive = useMemo(() => {
    return campaigns.every((campaign) => isCampaignRunning(campaign))
  }, [campaigns])

  const [event, dispatchEventAction] = eventState
  const { setup, loading: setupLoading, isAnonUser, canAnonBoost } = useCampaignSetup()
  const {
    fetchSetup,
    publishSetup,
    fetchAccounts,
    setError: setSetupError,
  } = useCampaignSetupDispatch()
  const { fetchCurrentCampaignConfig } = useCampaignConfigDispatch()
  const { setCurrentStep, goToNextStep } = useCurrentStepDispatch()
  const { currentStep } = useCurrentStep()

  // Sync from URL → state
  useEffect(() => {
    const raw = searchParam.get('step')
    const parsed = raw ? parseInt(raw, 10) : 1
    const minStep = isAnonUser ? 2 : 1
    const maxStep = canAnonBoost ? 5 : 4
    const step = Math.min(
      Math.max(Number.isFinite(parsed) && parsed > 0 ? parsed : minStep, minStep),
      maxStep,
    )
    setCurrentStep(step, step)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync from state → URL
  useEffect(() => {
    const step = searchParam.get('step')
    const currentInUrl = step ? parseInt(step, 10) : NaN
    if (currentStep && currentStep > 0 && currentStep !== currentInUrl) {
      searchParam.set('step', String(currentStep))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  const isFutureEvent = useMemo(() => {
    return moment(event.content?.date) > moment()
  }, [event])

  useErrorHandler(event.error ? { userFriendlyMessage: event.message } : null)

  const handleResize = () => setViewWidth(window.innerWidth)

  useEffect(() => {
    if (currentStep !== 1) return

    // refetch default campaign parameters when the step is 1
    publishedCampaignSetupsApi.fetchDefaultParametersAsync(eventId).then(({ error, data }) => {
      if (!error && data) {
        setEventGoal(data.goal || DEFAULT_CAMPAIGN_GOAL)
        setEventBudget(data.total_budget || DEFAULT_CAMPAIGN_BUDGET)
      }
    })
  }, [currentStep, eventId])

  // Reset to step 1 ONLY when setup transitions from object → null (cancelled).
  // `publishSetupAsync` also dispatches setup=undefined on a successful boost
  // (the boost response carries no setup payload), which looks identical to a
  // cancellation from here - the currentStep===5 check disambiguates the two,
  // since goToNextStep() lands there in the same batch as the setup dispatch.
  const prevSetupRef = useRef(null)
  useEffect(() => {
    if (setupLoading) {
      prevSetupRef.current = setup
      return
    }

    const prev = prevSetupRef.current
    const wasObject = !!prev
    const isNowNull = !setup
    const minStep = isAnonUser ? 2 : 1
    const isAnonPublishedScreen = isAnonUser && currentStep === 5

    if (wasObject && isNowNull && currentStep !== minStep && !isAnonPublishedScreen) {
      setCurrentStep(minStep, minStep)
    }

    prevSetupRef.current = setup
  }, [setup, setupLoading, currentStep, setCurrentStep, isAnonUser])

  useEffect(() => {
    if (!eventId) return

    const updateDefaultCampaignParameters = async ({ error, data }) => {
      if (!error && data) {
        await setEventGoal(data.goal) // FIXME: Why??
        await setEventBudget(data.total_budget) // FIXME: Why??
      }
    }

    fetchEvent(dispatchEventAction, eventId)
    publishedCampaignSetupsApi
      .fetchDefaultParametersAsync(eventId)
      .then(updateDefaultCampaignParameters)
  }, [eventId, dispatchEventAction, resetKey])

  useEffect(() => {
    if (currentStep !== 5) return

    // Anon users have no app to return to - they stay on the static confirmation
    // screen until they close the tab, so there's nothing to refetch. Refetching
    // the setup here would also 404 (it's deleted once published) and trigger the
    // object→null reset effect above, bouncing them out of the confirmation screen.
    if (isAnonUser) return

    // setup was successfully published — land directly on the real Active
    // Campaigns view (step 1, RunningCampaigns) instead of a separate
    // confirmation screen; boost()'s success handler already toasts
    // confirmation, so no transitional banner is needed here (PD-3388-06-04).
    fetchEvent(dispatchEventAction, eventId)
    fetchSetup()
    setCurrentStep(1)
  }, [eventId, dispatchEventAction, currentStep, setCurrentStep, fetchSetup, t, isAnonUser])

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const onNext = () => {
    const maxStep = canAnonBoost ? 5 : 4
    const nextStep = Math.min(currentStep + 1, maxStep)
    setCurrentStep(nextStep, nextStep)
  }

  const onBack = () => {
    const minStep = isAnonUser ? 2 : 1
    setCurrentStep(Math.max(currentStep - 1, minStep))
  }

  // The boost covers every platform in the setup, so a validation failure names
  // them all rather than whichever one actually failed.
  const setupProviderNames = providerNamesForPlatforms(
    toApiPlatformField(derivePlatformSelectionFromSetup(setup)),
  )

  const boost = useCallback(() => {
    publishSetup(({ success, reason }) => {
      if (success) {
        fetchCurrentCampaignConfig()
        addNotification({
          message: t('Toasts.Campaigns.campaignPublished.message'),
          description: t('Toasts.Campaigns.campaignPublished.description'),
        })
        goToNextStep()
      } else {
        if (reason === 'INVALID_SETUP') {
          setSetupError(t('Recommendations.campaign.setup.unknownError'))
          setCurrentStep(3, 2)
        } else if (reason === 'INVALID_ACCOUNTS') {
          setSetupError(
            <Trans
              i18nKey="Recommendations.campaign.setup.invalidAccounts"
              components={{
                a: (
                  <a
                    href={SETTINGS_PLATFORMS_ANCHOR}
                    rel="noopener noreferrer"
                    target="_blank"
                    className="d-inline-block"
                    style={{ color: '#2F80ED' }}
                  >
                    {setupProviderNames} assets page
                  </a>
                ),
              }}
              values={{ provider: setupProviderNames }}
            >
              Validation failed. Please reconnect the assets on{' '}
              <a href={SETTINGS_PLATFORMS_ANCHOR}>{setupProviderNames} asset page</a> and grant us
              the necessary access.
            </Trans>,
          )
          setCurrentStep(2, 1)
        }
      }
    })
  }, [
    publishSetup,
    goToNextStep,
    addNotification,
    setSetupError,
    setCurrentStep,
    t,
    fetchCurrentCampaignConfig,
    setupProviderNames,
  ])

  // if mobile, redirect user to full screen views
  if (viewWidth < 991) {
    const isDrawerOpen = searchParam.get('drawer-open') === 'true'
    if (isDrawerOpen) {
      const currentStep = searchParam.get('step')
      const stepQuery = currentStep && currentStep > 0 ? `?step=${currentStep}` : '?step=1'
      return <Redirect to={`/campaigns/${eventId}${stepQuery}`} />
    }
  }

  if (!eventId) return null

  if (!event.success) return null

  if (currentStep === 5) {
    if (isAnonUser) {
      return (
        <div className="anon-campaign-published">
          <FdPublishedIcon className="anon-campaign-published_illustration" />
          <h1 className="anon-campaign-published_title">{t('AnonSetup.published.title')}</h1>
          <p className="anon-campaign-published_description">
            {t('AnonSetup.published.description')}
          </p>
        </div>
      )
    }

    // Non-anon: the effect above flips currentStep to 1 as soon as the
    // post-boost refetch kicks off, so this state is momentary — nothing to
    // render here in place of the old static "boosted!" banner (PD-3388-06-04).
    return null
  }

  return (
    <div>
      {isFutureEvent && !allCampaignsActive && <StepCompletionBar />}
      <Step
        event={event.content}
        initialGoal={eventGoal}
        initialBudget={eventBudget}
        onNext={onNext}
        onBack={onBack}
        boost={boost}
        setCampaigns={setCampaigns}
        isFutureEvent={isFutureEvent}
        fetchedCampaigns={fetchedCampaigns}
      />
    </div>
  )
}

CampaignStepWrapper.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  eventState: PropTypes.array.isRequired,
  resetKey: PropTypes.number,
  campaigns: PropTypes.arrayOf(object),
  setCampaigns: PropTypes.func,
  fetchedCampaigns: PropTypes.object,
}

export default CampaignStepWrapper
